import { Injectable } from "@angular/core";
import { TranslationService } from "@core-services/translation.service";
import { FiatService } from "@main-services/currency";
import { TradableListService } from "@main-services/currency/tradable_list.service";
import { FiatWalletService } from "@main-services/wallet/fiat_wallet.service";
import { TradableWalletListService } from "@main-services/wallet/tradable_wallet_list.service";
import { Fiat } from "@models/asset/fiat/fiat";
import { SavingPlanStatus, SavingsPlan } from "@models/savings_plan";
import { SavingsService } from "@main-services/savings.service";
import { UntilDestroy } from "@ngneat/until-destroy";
import { Observable, switchMap } from "rxjs";
import { map } from "rxjs/operators";
import { SavingsListViewModel } from "@modules/shared/domain/entities/savings-list-view.model";
import { SAVINGS_WIDGET_ITEM_LIMIT } from "@helpers/constants";
import { SavingsListViewAdapter } from "@modules/shared/domain/entities/savings-list-view-adapter";
import { Index } from "@models/asset/index/index.model";

@UntilDestroy()
@Injectable({
	providedIn: "root"
})
export class SavingsListFacade {
	constructor(
		private tradableListService: TradableListService,
		private tradableWalletListService: TradableWalletListService,
		private translationService: TranslationService,
		private fiatWalletService: FiatWalletService,
		private fiatService: FiatService,
		private savingsService: SavingsService
	) {}

	public getAllPlans(): Observable<SavingsPlan[]> {
		return this.savingsService.getList();
	}

	public getPlans(assetId: string = ""): Observable<SavingsPlan[]> {
		return this.savingsService.getList().pipe(
			map((list) =>
				this.sortSavingsPlans(
					list.filter((plan) => {
						const correctStatus: boolean = plan.getStatus() !== SavingPlanStatus.Canceled;
						return assetId.length > 0 ? correctStatus && assetId === plan.getCurrencyId() : correctStatus;
					})
				).slice(0, SAVINGS_WIDGET_ITEM_LIMIT)
			)
		);
	}

	private sortSavingsPlans(unsortedPlans: SavingsPlan[]): SavingsPlan[] {
		return unsortedPlans.sort((plan1, plan2) => {
			const byState = this.getStatePrecedence(plan1.getStatus()) - this.getStatePrecedence(plan2.getStatus());
			if (byState !== 0) {
				return byState;
			}

			return plan2.getCreatedAt().getUnix() - plan1.getCreatedAt().getUnix();
		});
	}

	public getSavingsListView$(assetId: string = ""): Observable<SavingsListViewModel[]> {
		return this.getPlans(assetId).pipe(
			switchMap((plans) => this.checkBffAssetsHelper(plans)),
			map((plans) => {
				return plans.map((plan) => {
					const adapter: SavingsListViewAdapter = new SavingsListViewAdapter(
						this.tradableListService,
						this.tradableWalletListService,
						this.translationService
					);
					return adapter.adapt(plan);
				});
			})
		);
	}

	private checkBffAssetsHelper(plans: SavingsPlan[]): Observable<SavingsPlan[]> {
		const missingPlans = this.savingsService.checkAssetsInPlan(plans);
		return this.savingsService.upsertBffAssetsReturnPlansHelper$(missingPlans, plans);
	}

	public getFiatId(plan: SavingsPlan): string {
		const wallet = this.fiatWalletService.getItemById(plan.getFiatWalletId());
		if (!wallet) {
			return "";
		}
		return wallet.getFiatId();
	}

	public getFiat(fiatId: string): Fiat {
		return this.fiatService.getItemById(fiatId);
	}
	public getFullPlanDetails(plan: SavingsPlan): void {
		this.savingsService.getFullPlanDetails(plan);
	}

	private getStatePrecedence(state: string): number {
		switch (state) {
			case SavingPlanStatus.Active.toString():
				return 0;
			case SavingPlanStatus.Pending.toString():
				return 1;
			case SavingPlanStatus.Paused.toString():
				return 2;
			default:
				return 3;
		}
	}

	public userHasNoBciSavingsPlans$(): Observable<boolean> {
		return this.getAllPlans().pipe(
			map((plans) => plans.filter((plan) => plan.getStatus() !== "canceled")),
			map((plans) => plans.map((plan) => plan.getCurrencyId())),
			map((tradableIds) => tradableIds.map((tradableId) => this.tradableListService.getItemById(tradableId))),
			map((tradables) => tradables.some((tradable) => tradable instanceof Index)),
			map((userhasBciSavingsPlans) => !userhasBciSavingsPlans)
		);
	}
}
